import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Service from "../Services/Service";
import loaderGif from "../Images/loader.gif";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button,
  Modal,
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
} from "@mui/material";
import ModeEditOutlineRoundedIcon from "@mui/icons-material/ModeEditOutlineRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

let vUserMapID = 0;

const UserMapping = () => {
  const [loading, setLoading] = useState(false);
  const [userMapping, setUserMapping] = useState([]);
  const [optionsUsr, setOptionsUser] = useState([]);
  const [optionsGrp, setOptionsGroup] = useState([]);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);

  // Controlled states for form
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [searchText, setSearchText] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    Service.setApiUrl(sessionStorage.getItem("APIUrl") + "/api/UserMapping");
    //Service.setApiUrl("https://localhost:7106/api/UserMapping");
    fetchUserMapping();
    bindUsername();
    bindGroupName();
  }, []);

  const navigate = useNavigate();

  const fetchUserMapping = async () => {
    const res = await Service.getAll();
    setUserMapping(res.data);
  };

  const bindUsername = async () => {
    const res = await Service.loadUsername(0, "User");
    setOptionsUser(res.data);
  };

  const bindGroupName = async () => {
    const res = await Service.loadUserGroup(0, "Group");
    setOptionsGroup(res.data);
  };

  const clear = () => {
    vUserMapID = 0;
    setSelectedUser("");
    setSelectedGroup("");
    setEditing(false);
  };
  const handleModalClose = () => {
    setOpen(false);
    setSearchText("");
  };

  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Duplicate employee validation
    const isDuplicate = userMapping.some(
      (item) =>
        String(item.empNo) === String(selectedUser) &&
        (!editing || String(item.userMapID) !== String(vUserMapID)),
    );

    if (isDuplicate) {
      setOpen(false);
      setSearchText("");

      showNotification(
        "This employee is already mapped with another role.",
        "error",
      );

      return;
    }

    let strMsg = editing
      ? "User mapping updated successfully"
      : "User mapped successfully";

    setLoading(true);

    try {
      await Service.create({
        UserMapID: vUserMapID,
        GroupId: selectedGroup,
        EmpNo: selectedUser,
        OpFlag: editing ? "Update" : "Insert",
      });

      clear();
      setOpen(false);
      setSearchText(""); // Clear search

      await fetchUserMapping();

      showNotification(strMsg, "success");
    } catch (error) {
      console.error("Error saving mapping:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.title ||
        error.message ||
        "Something went wrong while saving user mapping.";

      setOpen(false);

      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userMap) => {
    vUserMapID = userMap.userMapID;
    setSelectedGroup(userMap.groupId);
    setSelectedUser(userMap.empNo);
    setEditing(true);
    setOpen(true);
  };

  const handleAddClick = () => {
    clear();
    setOpen(true);
  };

  const handleDelete = async (vUserMapID, opFlag) => {
    //debugger;
    if (window.confirm("Are you sure to delete this mapped user?")) {
      await Service.remove(vUserMapID, opFlag);
      showNotification("User group deleted successfully", "success");
      fetchUserMapping();
    }
  };

  const filteredUserMapping = userMapping.filter((row) =>
    [row.empNo, row.empName, row.postName, row.department, row.groupName]
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase().trim()),
  );

  // 🔹 DataGrid Columns
  const columns = [
    {
      field: "serialNo",
      headerName: "No.",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    { field: "userMapID", headerName: "ID" },
    { field: "groupId", headerName: "Group ID" },
    { field: "empNo", headerName: "Employee No", flex: 1 },
    { field: "empName", headerName: "Employee Name", flex: 1 },
    { field: "postName", headerName: "Post", flex: 1 },
    { field: "department", headerName: "Department", flex: 1 },
    { field: "groupName", headerName: "Role", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<ModeEditOutlineRoundedIcon />}
            onClick={() => handleEdit(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteRoundedIcon />}
            onClick={() => handleDelete(params.row.userMapID, "Delete")}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <div
      style={{ height: 600, width: "100%", paddingLeft: 60, paddingRight: 60 }}
    >
      {/* <Button variant="contained" onClick={handleAddClick} sx={{ mb: 2 }}>
                Add User Mapping
            </Button> */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/DashboardFinal")}
        >
          Back to Home
        </Button>

        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder="Search Employee..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              width: 280,
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-root": {
                height: "36.5px",
              },
            }}
          />

          <Button variant="contained" onClick={handleAddClick}>
            Add User Mapping
          </Button>
        </Box>
      </Box>

      {/* MUI DataGrid with filtering + sorting + toolbar */}
      <DataGrid
        rows={filteredUserMapping}
        columns={columns}
        columnVisibilityModel={{
          userMapID: false,
          groupId: false,
        }}
        getRowId={(row) => row.userMapID}
        pageSize={5}
        rowsPerPageOptions={[5, 10, 20]}
        disableSelectionOnClick
        components={{ Toolbar: GridToolbar }}
      />

      {/* Modal for Add/Edit */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            width: 400,
            boxShadow: 4,
          }}
        >
          <h4>{editing ? "Edit" : "Add"} User Mapping</h4>
          <form onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={selectedUser}
                label="User"
                onChange={(e) => setSelectedUser(e.target.value)}
                required
              >
                <MenuItem value="">
                  <em>-- Select an option --</em>
                </MenuItem>
                {optionsUsr.map((u) => (
                  <MenuItem key={u.empNo} value={u.empNo}>
                    {u.empName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Group"
                onChange={(e) => setSelectedGroup(e.target.value)}
                required
              >
                <MenuItem value="">
                  <em>-- Select an option --</em>
                </MenuItem>
                {optionsGrp.map((g) => (
                  <MenuItem key={g.groupId} value={g.groupId}>
                    {g.groupName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" justifyContent="flex-end">
              <Button onClick={() => setOpen(false)} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                {editing ? "Update" : "Add"}
              </Button>
            </Box>
          </form>

          {loading && (
            <div className="loader-overlay">
              <img src={loaderGif} alt="Loading..." className="loader-img" />
            </div>
          )}
        </Box>
      </Modal>
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: 280,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserMapping;
